import React, { useEffect, useRef } from "react"
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Link,
  chakra,
  useDisclosure,
  useOutsideClick,
} from "@chakra-ui/react"
import { Link as GatsbyLink } from "gatsby"
import MenuLink from "./menu-link"
import { sections, summit, login, mobile, mobileQuery } from "./menu"

const border = "1px solid #E4E7EC"

// Let's Talk and Menu sit side by side, so they share a size.
const button = {
  display: "inline-flex",
  alignItems: "center",
  height: "44px",
  px: "14px",
  borderRadius: "4px",
  fontSize: "15px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  "@media (max-width: 600px)": { px: "12px", fontSize: "14px" },
}

const styles = {
  toggle: {
    display: "none",
    alignItems: "center",
    gap: "10px",
    [mobile]: { display: "flex" },
  },
  "talk-button": {
    ...button,
    bg: "#129459",
    color: "white",
    _hover: { bg: "#0E7748", color: "white", textDecoration: "none" },
  },
  "menu-button": {
    ...button,
    border: "1px solid #D0D5DD",
    bg: "white",
    color: "#041424",
    cursor: "pointer",
  },
  panel: {
    maxHeight: "calc(100vh - 76px)",
    overflow: "auto",
    px: "24px",
    pt: "8px",
    pb: "20px",
    borderTop: border,
    bg: "white",
  },
  item: {
    border: "none",
    borderBottom: border,
  },
  heading: {
    justifyContent: "space-between",
    px: 0,
    py: "14px",
    color: "#041424",
    fontWeight: 700,
    _hover: { bg: "transparent" },
  },
  sign: {
    color: "#5A6472",
    fontWeight: 400,
  },
  "section-links": {
    p: 0,
  },
  "section-link": {
    display: "block",
    py: "8px",
    pl: "12px",
    color: "#1A202C",
    fontSize: "15px",
    _hover: { color: "#1A202C", textDecoration: "none" },
  },
  "row-link": {
    display: "block",
    py: "14px",
    borderBottom: border,
    color: "#041424",
    fontWeight: 700,
    _hover: { color: "#041424", textDecoration: "none" },
  },
}

const activeStyle = { color: "#129459", fontWeight: 700 }

// Open state for the mobile menu. It closes on Escape, on clicks outside the
// header, and when the window widens past the mobile breakpoint.
export const useMobileMenu = () => {
  const ref = useRef(null)
  const { isOpen, onClose, onToggle } = useDisclosure()

  useOutsideClick({ ref, handler: onClose })

  useEffect(() => {
    if (!isOpen) return

    const media = window.matchMedia(mobileQuery)
    const closeOnDesktop = event => !event.matches && onClose()
    const closeOnEscape = event => event.key === "Escape" && onClose()

    media.addEventListener("change", closeOnDesktop)
    document.addEventListener("keydown", closeOnEscape)

    return () => {
      media.removeEventListener("change", closeOnDesktop)
      document.removeEventListener("keydown", closeOnEscape)
    }
  }, [isOpen, onClose])

  return { ref, isOpen, onToggle }
}

export const MobileMenuToggle = ({ isOpen, onToggle }) => (
  <Flex sx={styles.toggle}>
    <Link as={GatsbyLink} to="/contact-us/" sx={styles["talk-button"]}>
      Let's Talk
    </Link>
    <chakra.button
      type="button"
      aria-expanded={isOpen}
      onClick={onToggle}
      sx={styles["menu-button"]}
    >
      {isOpen ? "Close" : "Menu"}
    </chakra.button>
  </Flex>
)

// Only rendered while open, so it's safe to read the current path here.
const MobileMenu = ({ isOpen }) => {
  if (!isOpen) return null

  const path = window.location.pathname.replace(/\/?$/, "/")
  const current = sections.findIndex(section =>
    section.links.some(link => link.to === path)
  )

  return (
    <Box as="nav" aria-label="Mobile" sx={styles.panel}>
      <Accordion allowMultiple defaultIndex={current === -1 ? [] : [current]}>
        {sections.map(section => (
          <AccordionItem key={section.title} sx={styles.item}>
            {({ isExpanded }) => (
              <>
                <AccordionButton sx={styles.heading}>
                  {section.title}
                  <Box as="span" aria-hidden="true" sx={styles.sign}>
                    {isExpanded ? "−" : "+"}
                  </Box>
                </AccordionButton>
                <AccordionPanel sx={styles["section-links"]}>
                  {section.links.map(link => (
                    <MenuLink
                      key={link.label}
                      link={link}
                      activeStyle={activeStyle}
                      sx={styles["section-link"]}
                    />
                  ))}
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
        ))}
      </Accordion>
      <MenuLink link={summit} sx={styles["row-link"]} />
      <MenuLink link={login} sx={styles["row-link"]} />
    </Box>
  )
}

export default MobileMenu
